import { useEffect, useState } from "react";
import { Controller, useForm } from "@pankod/refine-react-hook-form";
import {
  Edit,
  Box,
  TextField,
  Grid,
  Autocomplete,
  useAutocomplete,
  RefreshButton,
} from "@pankod/refine-mui";
import { API_URL, typesOrder } from "../../constants";
import { ICatalog, IClient } from "interfaces";
import FileInput from "components/FileInput";
import { useParams } from "@pankod/refine-react-router-v6";
import { getTodayDate, uploadFiles } from "functions";
import { useCustom, useNavigation, useTranslate } from "@pankod/refine-core";
import { Previewers } from "components/Previewers";

export const OrderEdit: React.FC = () => {
  const { list } = useNavigation();
  const translate = useTranslate();
  const params = useParams();
  const { id } = params as any;

  const [files, setFiles] = useState([]);

  const {
    refineCore: { formLoading, onFinish, queryResult },
    saveButtonProps,
    register,
    control,
    watch,
    setError,
    clearErrors,
    formState: { errors },
    handleSubmit,
  } = useForm({
    refineCoreProps: {
      resource: "orders",
      redirect: false,
      id: id,
      metaData: {
        populate: ["client", "catalog", "order_file"],
      },
      onMutationSuccess: () => {
        list("orders/open");
      },
      successNotification: () => {
        return {
          description: translate("notifications.success"),
          message: translate("orders.edit.success"),
          type: "success",
        };
      },
    },
  });

  const data = queryResult?.data?.data;

  const { autocompleteProps } = useAutocomplete<IClient>({
    resource: "clients",
    defaultValue: data?.client?.id?.toString(),
  });

  const { autocompleteProps: autocompletePropsCatalogs } =
    useAutocomplete<ICatalog>({
      resource: "catalogs",
      defaultValue: data?.catalog?.id?.toString(),
    });

  const remove = (id: number) => {
    const newFiles = files.filter((_: any, index: number) => index !== id);
    setFiles(newFiles);
  };

  const { data: orders } = useCustom<any>({
    url: `${API_URL}/api/orders`,
    method: "get",
    config: {
      filters: [
        {
          field: "order",
          operator: "eq",
          value: watch("order"),
        },
        {
          field: "type",
          operator: "eq",
          value: watch("type"),
        },
        {
          field: "id",
          operator: "ne",
          value: id,
        },
      ],
    },
  });

  useEffect(() => {
    if (orders?.data?.data?.length === 0) {
      clearErrors("order");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orders]);

  const onSubmit = async (data: any) => {
    if (orders?.data?.data?.length > 0) {
      setError("order", {
        type: "manual",
        message: "Ya existe una orden con este número",
      });
    } else {
      const ids = await uploadFiles(files);

      onFinish({
        ...data,
        status: "Orden",
        order_file: ids,
      });
    }
  };

  useEffect(() => {
    const files = data?.order_file;
    if (files) {
      let dataFiles = [];
      if (Array.isArray(files)) {
        dataFiles = files.map((file) => {
          return {
            id: file?.id,
            path: file?.name,
            size: file?.size * 1024,
            url: file?.url,
            ext: file?.ext,
          };
        });
      } else {
        const file = {
          id: files?.id,
          path: files?.name,
          size: files?.size * 1024,
          url: files?.url,
          ext: files?.ext,
        };
        dataFiles.push(file);
      }
      setFiles(dataFiles as any);
    }
  }, [data]);

  return (
    <Edit
      isLoading={formLoading}
      saveButtonProps={{
        ...saveButtonProps,
        disabled: formLoading,
        onClick: handleSubmit(onSubmit),
      }}
      resource="orders"
      headerButtons={() => {
        return (
          <RefreshButton
            resourceNameOrRouteName="orders"
            recordItemId={id}
            metaData={{ populate: ["employee"] }}
          />
        );
      }}
    >
      <Box
        component="form"
        sx={{ display: "flex", flexDirection: "column" }}
        autoComplete="off"
      >
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <Controller
              control={control}
              name="type"
              rules={{ required: "Tipo es obligatorio" }}
              defaultValue={null as any}
              render={({ field }) => {
                return (
                  <Autocomplete
                    options={typesOrder}
                    {...field}
                    onChange={(_, value) => {
                      field.onChange(value?.name);
                    }}
                    getOptionLabel={(item) => {
                      return (
                        typesOrder?.find(
                          (p) => p?.name === item?.name || p?.name === item
                        )?.name ?? ""
                      );
                    }}
                    isOptionEqualToValue={(option, value) => {
                      return (
                        value === undefined ||
                        option.name === value.name ||
                        option.name === value
                      );
                    }}
                    renderInput={(params) => {
                      return (
                        <TextField
                          {...params}
                          label="Tipo"
                          margin="normal"
                          variant="outlined"
                          error={!!errors.type}
                          helperText={errors.type?.message?.toString()}
                          required
                        />
                      );
                    }}
                  />
                );
              }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              {...register("order", { required: "# de orden es obligatorio" })}
              error={!!errors?.order}
              helperText={errors.order?.message?.toString()}
              margin="normal"
              required
              fullWidth
              id="order"
              label="# de orden"
              name="order"
              defaultValue={" "}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Controller
              control={control}
              name="catalog"
              // rules={{ required: "El campo sku es obligatorio." }}
              defaultValue={null as any}
              render={({ field }) => {
                return (
                  <Autocomplete
                    {...autocompletePropsCatalogs}
                    {...field}
                    onChange={(_, value) => {
                      field.onChange(value);
                    }}
                    getOptionLabel={(item) => {
                      return (
                        autocompletePropsCatalogs?.options?.find(
                          (p) => p?.id?.toString() === item?.id?.toString()
                        )?.sku ?? ""
                      );
                    }}
                    isOptionEqualToValue={(option, value) => {
                      return (
                        value === undefined ||
                        option.id.toString() === value.id.toString()
                      );
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Sku"
                        margin="normal"
                        variant="outlined"
                        error={!!errors.catalog}
                        helperText={errors.catalog?.message?.toString()}
                      />
                    )}
                  />
                );
              }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Controller
              control={control}
              name="client"
              rules={{ required: "El campo cliente es obligatorio." }}
              defaultValue={null as any}
              render={({ field }) => {
                return (
                  <Autocomplete
                    {...autocompleteProps}
                    {...field}
                    onChange={(_, value) => {
                      field.onChange(value);
                    }}
                    getOptionLabel={(item) => {
                      return (
                        autocompleteProps?.options?.find(
                          (p) => p?.id?.toString() === item?.id?.toString()
                        )?.name ?? ""
                      );
                    }}
                    isOptionEqualToValue={(option, value) => {
                      return (
                        value === undefined ||
                        option.id.toString() === value.id.toString()
                      );
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Cliente"
                        margin="normal"
                        variant="outlined"
                        error={!!errors.client}
                        helperText={errors.client?.message?.toString()}
                        required
                      />
                    )}
                  />
                );
              }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              {...register("delivery_date", {
                required: "El campo fecha de entrega es obligatorio.",
              })}
              error={!!errors?.delivery_date}
              helperText={errors.delivery_date?.message?.toString()}
              margin="normal"
              fullWidth
              type={"date"}
              id="delivery_date"
              label="Fecha de entrega"
              name="delivery_date"
              defaultValue={getTodayDate()}
              required
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              {...register("final_client")}
              id="final_client"
              margin="normal"
              fullWidth
              name="final_client"
              label={"Cliente final"}
              helperText={errors?.final_client?.message?.toString()}
              error={!!errors.final_client}
              defaultValue={" "}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              {...register("total_pieces", {
                required: "El campo tpiezas totales es obligatorio.",
              })}
              id="total_pieces"
              margin="normal"
              fullWidth
              name="total_pieces"
              label={"Piezas totales"}
              helperText={errors?.total_pieces?.message?.toString()}
              error={!!errors.total_pieces}
              required
              defaultValue={" "}
            />
          </Grid>
          <Grid item xs={12} sm={12}>
            <FileInput
              setValue={setFiles}
              remove={remove}
              acceptedFiles={files}
              multiple={false}
            />
          </Grid>
        </Grid>
      </Box>
      <Previewers files={files} />
    </Edit>
  );
};
